/**
 * Prepares an image for upload: compresses photos, and strips metadata that
 * would otherwise get the upload blocked.
 *
 * PNG is the deliberate exception to *compression*, not an oversight:
 * screenshots are PNG, PNG is lossless, and re-encoding one either keeps it
 * lossless (saving little) or converts it to JPEG and softens exactly the text
 * and thin UI lines that made the screenshot worth sharing. JPEG and WebP are
 * camera/photo formats with no such text-legibility expectation, so they are
 * compressed on every upload — the same tradeoff WhatsApp makes by default.
 * That compression re-encodes them, which also drops their metadata.
 *
 * PNGs are not compressed, so their metadata survives — and AI image tools embed
 * C2PA "content credentials" that include an `<svg>` icon, which a firewall in
 * front of the server flags as cross-site-scripting and blocks with a 403 before
 * the request reaches the API. So a PNG carrying such metadata is re-encoded
 * losslessly to strip it (see strip-image-metadata.ts); a clean PNG is untouched.
 */
import { stripPngMetadata } from "./strip-image-metadata.js";

/** The longest edge after resizing. Comfortably bigger than a laptop screen. */
const MAX_EDGE = 2560;

/** Re-encode quality. High enough that the loss is not obvious at normal zoom. */
const QUALITY = 0.85;

/** Only these are recompressed. PNG is excluded on purpose — see above. */
const COMPRESSIBLE = new Set(["image/jpeg", "image/webp"]);

/**
 * Returns an upload-ready file, or the original when it should be left alone.
 *
 * Never throws: an upload failing because the *optimisation* failed would be a
 * worse outcome than a large upload, so every failure path returns the file
 * untouched.
 */
export async function downscaleImage(file: File): Promise<File> {
  // PNGs skip compression but still get their metadata stripped when they carry
  // any — this is what unblocks AI-generated PNGs at the firewall.
  if (!COMPRESSIBLE.has(file.type)) return stripPngMetadata(file);

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Corrupt, or a format this browser cannot decode. The server validates
    // magic bytes either way, so let it decide.
    return file;
  }

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), file.type, QUALITY);
    });

    // Guard against the pathological case where re-encoding grew the file —
    // a small, already-compressed image re-encoded at a fixed quality can.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], file.name, { type: file.type, lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}
